import { isNil } from 'lodash';
import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import { IChildrenProps } from 'types/ChildrenProps';

export const LoaderContext = createContext<[ILoaderState, React.Dispatch<Action>] | null>(null);

export function useLoaderState() {
  const context = useContext(LoaderContext);

  if (isNil(context)) {
    throw new Error('useLoader must be nested within a LoaderContext.Provider.');
  }

  return context;
}

type Action = { type: 'OPEN'; payload: { label: string } } | { type: 'CLOSE' };

export function useLoader() {
  const [, dispatch] = useLoaderState();

  return useMemo(
    () => ({
      open: (label: string) => {
        dispatch({ type: 'OPEN', payload: { label } });
      },
      close: () => {
        dispatch({ type: 'CLOSE' });
      },
    }),
    [dispatch]
  );
}

function loaderReducer(state: ILoaderState, action: Action): ILoaderState {
  if (action.type === 'OPEN') {
    return { isOpen: true, label: action.payload.label };
  } else if (action.type === 'CLOSE') {
    if (!state.isOpen) return state;
    return { ...state, isOpen: false };
  }

  return state;
}

interface ILoaderState {
  isOpen: boolean;
  label: string;
}

export function LoaderContextProvider(props: IChildrenProps) {
  const reducer = useReducer(loaderReducer, { isOpen: false, label: '' });
  const [, dispatch] = reducer;
  const { pathname } = useLocation();

  useEffect(() => {
    dispatch({ type: 'CLOSE' });
  }, [dispatch, pathname]);

  return <LoaderContext.Provider value={reducer}>{props.children}</LoaderContext.Provider>;
}
